import {
    AnyUpsellAddon,
    FerryAddonTypes,
    AvailableAddonType,
    MyBookingSessionData,
    MyBookingSessionEndPoint,
    ResponseStatus,
    SaveMyBookingSession,
    SaveMyBookingSessionResponse,
    UpSellAddonFerry,
    UpSellAddonBaggage,
} from './myBookingSession.interfaces';
import { fakeData } from './myBookingSession.fake';
import { delay } from '@shared/utils/async';
import { HttpEndpoint } from '@/api/httpEndpoints';
import { ProductType } from '@/api/Cart/cart.interfaces';
import { sample } from 'lodash';
import {
    getAddonsByType,
    mapBaggageAddonExcludingExtras,
} from '@/api/MyBookingSession/myBookingSession.helpers';
import myBookingSessionFakeJSON from '@/api/MyBookingSession/myBookingSession.fake.json';

export class MyBookingSessionEndpoint
    extends HttpEndpoint
    implements MyBookingSessionEndPoint
{
    public constructor(applicationUrl: string) {
        super(applicationUrl);
    }

    public async GetBookingSession(
        bookingId: string,
        password: string
    ): Promise<MyBookingSessionData> {
        const response = await this.get<MyBookingSessionData>(
            this.buildPathWithQuery('mvc/MyBookingPage/GetSessionInfo', {
                bookingId,
                password,
            }),
            { withCredentials: true }
        );

        return response;
    }

    public async SaveBookingSession(
        bookingId: string,
        password: string,
        addons: AnyUpsellAddon[]
    ): Promise<SaveMyBookingSession> {
        const baggageAddons = getAddonsByType(addons, AvailableAddonType.Baggage).map(
            (addon) => mapBaggageAddonExcludingExtras(addon as UpSellAddonBaggage)
        );
        const seatingAddons = getAddonsByType(addons, AvailableAddonType.Seating);
        const ferryAddons = getAddonsByType(addons, ProductType.Ferry);
        const servicePackageAddons = getAddonsByType(
            addons,
            AvailableAddonType.ServicePackage
        );
        const travelInsuranceAddons = getAddonsByType(
            addons,
            AvailableAddonType.TravelInsurance
        );

        const response = await this.postWithResponse<SaveMyBookingSessionResponse>(
            'mvc/MyBookingPage/SaveSessionInfo',
            {
                BookingId: bookingId,
                Password: password,
                Baggage: baggageAddons,
                Seating: seatingAddons,
                ServicePackage: servicePackageAddons,
                TravelInsurance: travelInsuranceAddons,
                Ferry: ferryAddons,
            },
            { withCredentials: true }
        );

        const errors = response.data?.Errors ?? [];

        if (response.status === 302) {
            return {
                status: ResponseStatus.BookingSuccess,
                errors,
            };
        } else if (errors.length > 0) {
            // TODO: check if we need translations for errors?
            return { status: ResponseStatus.Error, errors };
        }
        return { status: ResponseStatus.Redirect, errors };
    }

    public async ClearBookingSession(): Promise<void> {
        return await this.post<void>('mvc/MyBookingPage/ClearBasket', {
            withCredentials: true,
        });
    }

    public async SendSpecialRequestMessage(
        bookingId: string,
        password: string,
        message: string,
        messageCategory: string
    ): Promise<void> {
        return await this.post(
            'mvc/MyBookingPage/AddServicePackageMessage',
            {
                BookingId: bookingId,
                Password: password,
                Message: message,
                MessageCategory: messageCategory,
            },
            { withCredentials: true }
        );
    }
}

export class FakeMyBookingSessionEndpoint implements MyBookingSessionEndPoint {
    public async GetBookingSession(): Promise<MyBookingSessionData> {
        await delay(1000);

        const hasJSON =
            myBookingSessionFakeJSON && Object.keys(myBookingSessionFakeJSON).length > 0;

        if (hasJSON) {
            return myBookingSessionFakeJSON as any;
        } else {
            return fakeData;
        }
    }

    public async SaveBookingSession(
        bookingId: string,
        password: string,
        addons: AnyUpsellAddon[]
    ): Promise<SaveMyBookingSession> {
        await delay(1000);

        const ferryAddons: FerryAddonTypes[] = [
            AvailableAddonType.Meal,
            AvailableAddonType.Seat,
            AvailableAddonType.Pet,
            AvailableAddonType.Cabin,
            AvailableAddonType.DeckSpace,
            AvailableAddonType.Lounge,
        ];

        const hasFerryOnly = addons.every((x) => ferryAddons.includes(x.Type as any));

        if (hasFerryOnly) {
            const hasCost = (addons as UpSellAddonFerry[]).some((x) => {
                const amount = x.Price && x.Price.Amount ? x.Price.Amount : 0;
                return amount > 0;
            });

            if (!hasCost) {
                const showErrors = sample([0, 1]) === 1;

                if (showErrors) {
                    return {
                        errors: ['Please make a selection for all passengers'],
                        status: ResponseStatus.Error,
                    };
                } else {
                    return {
                        errors: [],
                        status: ResponseStatus.BookingSuccess,
                    };
                }
            }
        }

        return {
            errors: [],
            status: ResponseStatus.Redirect,
        };
    }

    public async ClearBookingSession(): Promise<void> {
        return await delay(1000);
    }

    public async SendSpecialRequestMessage(): Promise<void> {
        return await delay(3000);
    }
}
